/**
 * ProShip Direct v2 — rate card upload: template, parse, validate.
 *
 * Pure functions, no React, so the validation is testable and the page stays presentational.
 *
 * The card is the one setup dimension with no ProShip default behind it. ProShip's rates are
 * ProShip's cost, not the merchant's, so standing them in would put a number on the comparison
 * screen that is not the merchant's number — worse than showing no number. That is why a missing
 * card leaves cost blank rather than falling back.
 *
 * Slab labels are taken as written rather than matched against a fixed list. A contract that
 * prices "Up to 250g" or "5 – 10kg" is a normal contract, and rejecting it for not matching our
 * five slabs would reject the merchants this feature exists for.
 *
 * DEVIATION: a light CSV read (split on newline, then comma), matching ruleCsv.js and zoneCsv.js.
 * A slab label containing a comma would therefore be misread, so any row whose column count is
 * wrong is rejected outright rather than guessed at.
 */

export const RATE_ZONES = ['Zone 1', 'Zone 2', 'Zone 3', 'Zone 4', 'Zone 5'];

export const RATE_CSV_COLUMNS = ['weight_slab', ...RATE_ZONES.map((z) => z.toLowerCase().replace(' ', '_'))];

/** Slabs in the template. A merchant's own slabs are accepted in their place. */
const TEMPLATE_SLABS = ['Up to 500g', '500g - 1kg', '1 - 2kg', '2 - 5kg', 'Each additional kg'];

export const RATE_CSV_TEMPLATE = [
  RATE_CSV_COLUMNS.join(','),
  ...TEMPLATE_SLABS.map((slab) => [slab, '', '', '', '', ''].join(',')),
].join('\n');

/** How many slabs this build keeps. A rate card with more rows than this is not a rate card. */
export const RATE_ROW_CAP = 60;

const isAmount = (value) => /^\d+(\.\d{1,2})?$/.test(String(value || '').trim());

/**
 * Parses a rate-card CSV.
 *
 * Returns `{ ok, error, slabs, errors, summary }`. `error` is set only when the file itself is
 * unusable (wrong header, no rows); individual bad rows come back in `errors` with their line
 * number, and the good rows still come back — a merchant fixing two slabs should not have to
 * re-export the file.
 */
export function parseRateCsv(text) {
  const lines = String(text || '')
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter(Boolean);

  if (lines.length < 2) {
    return { ok: false, error: 'The file has a header but no rate rows.', slabs: [], errors: [], summary: null };
  }

  const header = lines[0].split(',').map((h) => h.trim().toLowerCase());
  if (header.length !== RATE_CSV_COLUMNS.length || RATE_CSV_COLUMNS.some((c, i) => header[i] !== c)) {
    return {
      ok: false,
      error: `Header must be exactly: ${RATE_CSV_COLUMNS.join(', ')}. Download the template and fill that in.`,
      slabs: [],
      errors: [],
      summary: null,
    };
  }

  const slabs = [];
  const errors = [];
  const seen = new Set();

  lines.slice(1).forEach((line, i) => {
    const lineNo = i + 2; // 1-indexed, and the header is line 1
    const cells = line.split(',').map((c) => c.trim());

    if (cells.length !== RATE_CSV_COLUMNS.length) {
      errors.push({ line: lineNo, message: `Expected ${RATE_CSV_COLUMNS.length} columns, found ${cells.length}` });
      return;
    }

    const [slab, ...amounts] = cells;

    if (!slab) {
      errors.push({ line: lineNo, message: 'The weight slab is blank' });
      return;
    }
    if (seen.has(slab.toLowerCase())) {
      errors.push({ line: lineNo, message: `"${slab}" appears more than once` });
      return;
    }

    const blank = amounts.findIndex((a) => a === '');
    if (blank !== -1) {
      errors.push({ line: lineNo, message: `${slab}: ${RATE_ZONES[blank]} has no rate` });
      return;
    }

    const bad = amounts.findIndex((a) => !isAmount(a));
    if (bad !== -1) {
      errors.push({ line: lineNo, message: `${slab}: "${amounts[bad]}" is not an amount` });
      return;
    }

    seen.add(slab.toLowerCase());
    slabs.push({ slab, rates: amounts.map(Number) });
  });

  const all = slabs.flatMap((s) => s.rates);

  return {
    ok: slabs.length > 0,
    error: slabs.length === 0 ? 'Every row was rejected — nothing to save.' : null,
    slabs,
    errors,
    summary: {
      slabCount: slabs.length,
      rejected: errors.length,
      lowest: all.length ? Math.min(...all) : 0,
      highest: all.length ? Math.max(...all) : 0,
      truncated: Math.max(0, slabs.length - RATE_ROW_CAP),
    },
  };
}

/** Current card back out as CSV, so download → edit → upload is lossless. */
export function rateSlabsToCsv(slabs) {
  return [
    RATE_CSV_COLUMNS.join(','),
    ...(slabs || []).map((s) => [s.slab, ...s.rates].join(',')),
  ].join('\n');
}
